// Offline mode P1 — IndexedDB schema constants and record shapes.
// See docs/offline-mode-plan.md §8 for the sketch this implements.

export const OFFLINE_DB_NAME = 'impata-offline';
// This app is the sole owner of the schema; nothing else opens this database. Bumping it
// means extending OfflineDbService's onupgradeneeded to create/migrate whatever changed — a
// bump on its own creates nothing.
export const OFFLINE_DB_VERSION = 1;

// Bump this whenever a change makes ALREADY-SAVED records unusable or unreachable: a new key
// format, a different record shape, a change to how content is encrypted or chunked.
//
// It exists because OFFLINE_DB_VERSION cannot cover that case. A DB version bump can add or
// alter object STORES, but the rows that broke us before were sitting in stores that were
// perfectly valid — they were simply written under a key the new code no longer looks up, or
// with a field it no longer understands. IndexedDB has no opinion about that, so nothing
// noticed, and the only fix was a human clearing site data by hand on every device.
//
// OfflineDbService.ensureDataFormatCurrent() compares this against a stamp kept in the
// database and wipes the offline data when they differ, so a breaking change costs the student
// a re-download instead of costing you a support conversation. Only ever wipes DOWNLOADED
// copies, all of which can be fetched again.
export const OFFLINE_DATA_FORMAT = 1;
export const DATA_FORMAT_ID = 'data-format';

export const STORE_KEYS = 'keys';
export const STORE_ENTITLEMENT = 'entitlement';
export const STORE_CONTENT = 'content';
export const STORE_MEDIA = 'media';
export const STORE_INDEX = 'index';

export const DEVICE_KEY_ID = 'device-key';

export interface DeviceKeyRecord {
  id: 'device-key';
  key: CryptoKey;
  createdAt: number;
}

export interface EntitlementRecord {
  id: string; // `${school}:${user}`
  school: string;
  user: number;
  signedBlob: string;
  issuedAt: number;
  expiresAt: number;
  // Decoded locally from signedBlob at store time (offline-entitlement.service.ts) — the
  // module ids this entitlement actually covers, so the interceptor's fail-closed guard can
  // check per-resource, not just per-user.
  moduleIds: number[];
}

export interface ContentRecord {
  key: string; // `${school}:${user}:${kind}:${resourceId}:${version}`
  school: string;
  user: number;
  kind: string;
  resourceId: string;
  version: string;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
  savedAt: number;
  bytes: number;
}

// One manifest per saved video. The video itself is a plain file in OPFS (see
// offline-video-files.ts); this row records which file and which version.
//
// recordType 'chunk', and manifests without storage === 'opfs', are the previous design's
// encrypted 1 MiB rows. Nothing reads them any more; OfflineDbService.deleteLegacyMediaRows()
// removes them at startup. Their fields stay on this type only so that cleanup can name them.
export interface MediaRecord {
  key: string;
  recordType: 'manifest' | 'chunk';
  storage?: 'opfs';
  fileName?: string;
  school: string;
  user: number;
  resourceId: string;
  rendition: string;
  version: string;
  mimeType?: string;
  totalBytes?: number;
  chunkSizeBytes?: number;
  chunkCount?: number;
  index?: number;
  iv?: Uint8Array;
  ciphertext?: ArrayBuffer;
  savedAt?: number;
}

export interface IndexRecord {
  key: string; // `${school}:${user}:${kind}:${resourceId}`
  school: string;
  user: number;
  kind: string;
  resourceId: string;
  // Needed for the entitlement per-module check (offline-cache-interceptor.service.ts) —
  // an entitlement covers modules, not individual lessons/notes.
  moduleId: number;
  // Drives the P6 downloads screen's grouping — optional so existing/older rows don't need
  // a migration; treat missing as 'text'.
  kindGroup?: 'text' | 'files' | 'video';
  // The only role value this machinery ever persists — D5 restricts the whole feature to
  // non-admins, so nothing should ever reach a written row with any other role.
  role: 'student';
  status: 'saved' | 'saving' | 'error';
  currentVersion: string;
  sizeBytes: number;
  savedAt: number;
}
